import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Mail, Clock, MapPin, Hourglass, CreditCard } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SERVICES, BUSINESS_EMAIL, BUSINESS_CARD_STYLES, PORTRAIT_MEDIUMS, LOGO_TYPES } from "../data/site";

export default function Contact() {
  const location = useLocation();
  const preselect = location.state?.service || "";
  const qtyMatch = preselect.match(/\(qty (\d+)\)/);
  const initialStyle = preselect.includes("Square Style")
    ? "Square Style"
    : "Rectangular Style";
  const initialBookSize = preselect.includes("Comic Books") ? "6x9" : "6x6";

  const [form, setForm] = useState({
    name: "",
    service: preselect,
    cardStyle: initialStyle,
    qty: qtyMatch ? Number(qtyMatch[1]) : 25,
    units: 1,
    canvas: "12x36",
    medium: "",
    portraitSize: "",
    pages: "",
    format: "Physical Copy",
    binding: "Saddle Stitch",
    bookSize: initialBookSize,
    artStyle: "Watercolor",
    abstractStyle: "Geometric Abstraction",
    illustrationType: "Book Cover Design",
    logoType: "Wordmark",
    projectType: "Portrait Illusion",
    specialQty: 1,
    specialCards: 18,
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState("");

  const QTY_SERVICES = [
    "Business Cards",
    "Comic Books",
    "Children's Books",
    "Flyers & Posters",
    "Apparel & Merch",
  ];
  const UNIT_SERVICES = ["Custom Illustration", "Business Logos"];
  const QUANTITIES = [25, 50, 100, 250, 500, 1000];
  const UNIT_QUANTITIES = [1, 2, 3];
  const CARD_STYLES = ["Rectangular Style", "Square Style"];
  const CANVAS_SIZES = ["12x36", "24x36"];
  const ABSTRACT_STYLES = ["Geometric Abstraction", "Abstract Expressionism"];
  const ILLUSTRATION_TYPES = [
    "Book Cover Design",
    "Custom Character Design",
    "Custom Business & Brand Illustration",
  ];
  const needsIllustrationType = form.service.includes("Custom Illustration");
  const needsSpecial = form.service.includes("Special Projects");
  const needsLogoType = form.service.includes("Business Logos");
  const PROJECT_TYPES = [
    "Portrait Illusion",
    "Custom 3D Design",
    "Card Game Creator Package",
  ];
  const specialIsCardGame = form.projectType === "Card Game Creator Package";
  const needsQty = QTY_SERVICES.some((s) => form.service.includes(s));
  const needsUnits = UNIT_SERVICES.some((s) => form.service.includes(s));
  const needsCardStyle = form.service.includes("Business Cards");
  const needsCanvas = form.service.includes("Abstract Art");
  const needsPortrait = form.service.includes("Commission Portraits");
  const getPortraitSizes = (medium) => {
    if (medium === "Oil Paint") return ["11x14", "16x20"];
    if (medium === "Soft Pastel" || medium === "Charcoal")
      return ["6x9", "11x14", "16x20"];
    return [];
  };
  const portraitSizeOptions = getPortraitSizes(form.medium);
  const portraitSizeLabel = form.medium === "Oil Paint" ? "Canvas Size" : "Paper Size";
  const COMIC_PAGES = [{ value: 24, label: "24 Pages (Cover + 20 Inside)" }];
  const CHILDREN_PAGES = [8, 10, 12].map((n) => ({ value: n, label: String(n) }));
  const needsComicPages = form.service.includes("Comic Books");
  const needsChildrenPages = form.service.includes("Children's Books");
  let pagesOptions = [];
  if (needsComicPages) pagesOptions = COMIC_PAGES;
  else if (needsChildrenPages) pagesOptions = CHILDREN_PAGES;
  const hideQtyForEbook =
    (needsComicPages || needsChildrenPages) &&
    form.format === "Ebook (Digital Copy)";

  const EXCLUDED_SERVICES = ["Flyers & Posters", "Apparel & Merch"];
  const serviceOptions = [];
  SERVICES.forEach((s) => {
    if (!EXCLUDED_SERVICES.includes(s.title)) serviceOptions.push(s.title);
  });

  const computeEstimate = () => {
    const svc = form.service;
    if (!svc) return null;
    if (svc.includes("Business Logos")) {
      const lt = LOGO_TYPES.find((t) => t.name === form.logoType);
      const base = lt ? lt.price || 49 : 49;
      return base * form.units;
    }
    if (svc.includes("Business Cards")) {
      const st = BUSINESS_CARD_STYLES.find((s) => s.name === form.cardStyle);
      return st ? st.prices[form.qty] ?? null : null;
    }
    if (svc.includes("Abstract Art")) {
      const base = form.abstractStyle === "Abstract Expressionism" ? 349 : 229;
      return base + (form.canvas === "24x36" ? 120 : 0);
    }
    if (svc.includes("Commission Portraits")) {
      const med = PORTRAIT_MEDIUMS.find((m) => m.name === form.medium);
      if (!med) return null;
      const sz = med.sizes.find((s) => s.size === form.portraitSize);
      return sz ? sz.price : null;
    }
    if (svc.includes("Comic Books")) {
      const isHyper = form.artStyle === "Hyper-Detailed";
      if (form.format === "Ebook (Digital Copy)") return isHyper ? 2799 : 2499;
      const qp = { 25: 199, 50: 329, 100: 449, 250: 699 };
      const base = qp[form.qty];
      if (base === undefined) return null;
      return base + (form.bookSize === "6x9" ? 2790 : 0) + (isHyper ? 300 : 0);
    }
    if (svc.includes("Children's Books")) {
      const pp = { 8: 1249, 10: 1599, 12: 1869 };
      const base = pp[form.pages];
      if (base === undefined) return null;
      const hyper = form.artStyle === "Hyper-Detailed" ? 300 : 0;
      const physical = form.format === "Physical Copy";
      let binding = 0;
      if (physical && form.binding === "Softcover") binding = 200;
      else if (physical && form.binding === "Hardcover") binding = 350;
      const surcharge = { 25: 0, 50: 100, 100: 200, 250: 350 };
      const q = physical ? surcharge[form.qty] : 0;
      if (q === undefined) return null;
      return base + (form.bookSize === "6x9" ? 150 : 0) + binding + q + hyper;
    }
    if (svc.includes("Custom Illustration")) {
      const bases = {
        "Book Cover Design": 229,
        "Custom Character Design": 349,
        "Custom Business & Brand Illustration": 699,
      };
      const base = bases[form.illustrationType];
      if (base === undefined) return null;
      if (form.units === 1) return base;
      const q2 = base + 150;
      return form.units === 2 ? q2 : q2 + 220;
    }
    if (svc.includes("Special Projects")) {
      if (form.projectType === "Card Game Creator Package")
        return form.specialCards === 36 ? 2499 : 1849;
      if (form.projectType === "Portrait Illusion") {
        if (form.specialQty === 1) return 169;
        const q2 = 169 + 140;
        return form.specialQty === 2 ? q2 : q2 + 180;
      }
      if (form.projectType === "Custom 3D Design") {
        if (form.specialQty === 1) return 699;
        const q2 = 699 + 560;
        return form.specialQty === 2 ? q2 : q2 + 480;
      }
      return null;
    }
    return null;
  };

  const estimate = computeEstimate();

  const needsDeposit =
    form.service.includes("Comic Books") || form.service.includes("Children's Books");

  const handlePurchase = async () => {
    setPurchaseError("");
    if (!form.service) {
      setPurchaseError("Please choose what you need first.");
      return;
    }
    if (estimate == null) {
      setPurchaseError(
        "This selection needs a custom quote — please use the email option below."
      );
      return;
    }
    setPurchasing(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin, order: form }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Unable to start checkout. Please try again.");
      }
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received.");
      }
    } catch (e) {
      setPurchaseError(e.message || "Something went wrong. Please try again.");
      setPurchasing(false);
    }
  };

  return (
    <div className="App">
      <Navbar />

      <section className="bg-[#1F2530] text-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E5E7EB]">
            Get In Touch
          </span>
          <h1 className="font-display text-6xl sm:text-8xl leading-[0.85] mt-3">
            LET'S <span className="text-[#7BA7D9]">TALK</span>
          </h1>
          <p className="mt-5 max-w-xl text-lg font-medium text-[#F3F4F6]/70">
            Tell us about your project, then complete your order securely using a credit
            or debit card, Apple Pay, or Google Pay.
          </p>
        </div>
      </section>

      <section className="bg-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-10">
          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="brutal-border brutal-shadow bg-white p-6 sm:p-10">
              <h2 className="font-display text-4xl mb-6">Start an Order</h2>

              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                    Your Name
                  </label>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                    What do you need?
                  </label>
                  <select
                    data-testid="contact-service"
                    value={form.service}
                    onChange={(e) => {
                      const sv = e.target.value;
                      const p = sv.includes("Comic Books")
                        ? 24
                        : sv.includes("Children's Books")
                        ? 8
                        : "";
                      setForm((f) => ({
                        ...f,
                        service: sv,
                        pages: p,
                        binding: sv.includes("Comic Books") ? "Saddle Stitch" : f.binding,
                        bookSize: sv.includes("Comic Books") ? "6x9" : f.bookSize,
                        qty:
                          (sv.includes("Comic Books") || sv.includes("Children's Books")) &&
                          f.qty > 250
                            ? 25
                            : f.qty,
                      }));
                    }}
                    className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                  >
                    <option value="">Select a service…</option>
                    {serviceOptions.map((title) => (
                      <option key={title} value={title}>
                        {title}
                      </option>
                    ))}
                  </select>
                </div>

                {needsSpecial && (
                  <>
                    <div data-testid="contact-projecttype-wrap">
                      <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                        Project Type
                      </label>
                      <select
                        data-testid="contact-projecttype"
                        value={form.projectType}
                        onChange={update("projectType")}
                        className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                      >
                        {PROJECT_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>

                    {specialIsCardGame ? (
                      <div data-testid="contact-specialcards-wrap">
                        <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                          Card Quantity
                        </label>
                        <select
                          data-testid="contact-specialcards"
                          value={form.specialCards}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, specialCards: Number(e.target.value) }))
                          }
                          className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                        >
                          {[18, 36].map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div data-testid="contact-specialqty-wrap">
                        <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                          Quantity
                        </label>
                        <select
                          data-testid="contact-specialqty"
                          value={form.specialQty}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, specialQty: Number(e.target.value) }))
                          }
                          className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                        >
                          {[1, 2, 3].map((q) => (
                            <option key={q} value={q}>
                              {q}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}

                {(needsComicPages || needsChildrenPages) && (
                  <>
                    <div data-testid="contact-format-wrap">
                      <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                        Format
                      </label>
                      <select
                        data-testid="contact-format"
                        value={form.format}
                        onChange={update("format")}
                        className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                      >
                        <option value="Physical Copy">Physical Copy</option>
                        <option value="Ebook (Digital Copy)">Ebook (Digital Copy)</option>
                      </select>
                    </div>

                    {form.format === "Physical Copy" && (
                      <div data-testid="contact-binding-wrap">
                        <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                          Binding
                        </label>
                        <select
                          data-testid="contact-binding"
                          value={form.binding}
                          onChange={update("binding")}
                          className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                        >
                          {(form.service.includes("Comic Books")
                            ? ["Saddle Stitch"]
                            : ["Saddle Stitch", "Softcover", "Hardcover"]
                          ).map((b) => (
                            <option key={b} value={b}>
                              {b}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div data-testid="contact-booksize-wrap">
                      <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                        Book Size
                      </label>
                      <select
                        data-testid="contact-booksize"
                        value={form.bookSize}
                        onChange={update("bookSize")}
                        className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                      >
                        {(form.service.includes("Comic Books")
                          ? ["6x9"]
                          : ["6x6", "6x9"]
                        ).map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div data-testid="contact-artstyle-wrap">
                      <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                        Art Style
                      </label>
                      <select
                        data-testid="contact-artstyle"
                        value={form.artStyle}
                        onChange={update("artStyle")}
                        className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                      >
                        {["Watercolor", "Cartoon", "Hyper-Detailed"].map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {needsPortrait && (
                  <>
                    {form.medium && (
                      <div data-testid="contact-portraitsize-wrap">
                        <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                          {portraitSizeLabel}
                        </label>
                        <select
                          data-testid="contact-portraitsize"
                          value={form.portraitSize}
                          onChange={update("portraitSize")}
                          className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                        >
                          {portraitSizeOptions.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div data-testid="contact-medium-wrap">
                      <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                        Choose Your Medium
                      </label>
                      <select
                        data-testid="contact-medium"
                        value={form.medium}
                        onChange={(e) => {
                          const m = e.target.value;
                          const firstSize =
                            m === "Oil Paint"
                              ? "11x14"
                              : m === "Soft Pastel" || m === "Charcoal"
                              ? "6x9"
                              : "";
                          setForm((f) => ({ ...f, medium: m, portraitSize: firstSize }));
                        }}
                        className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                      >
                        <option value="">Select a medium…</option>
                        <option value="Oil Paint">Oil Paint</option>
                        <option value="Soft Pastel">Soft Pastel</option>
                        <option value="Charcoal">Charcoal</option>
                      </select>
                      {form.medium === "Oil Paint" && (
                        <p
                          data-testid="oil-paint-note"
                          className="mt-2 text-sm font-semibold italic text-red-600"
                        >
                          *Please Note: Oil paintings require a 7-day drying period before
                          shipping.
                        </p>
                      )}
                    </div>
                  </>
                )}

                {needsCanvas && (
                  <div data-testid="contact-abstractstyle-wrap">
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                      Art Style
                    </label>
                    <select
                      data-testid="contact-abstractstyle"
                      value={form.abstractStyle}
                      onChange={update("abstractStyle")}
                      className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                    >
                      {ABSTRACT_STYLES.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {needsCanvas && (
                  <div data-testid="contact-canvas-wrap">
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                      Canvas Size
                    </label>
                    <select
                      data-testid="contact-canvas"
                      value={form.canvas}
                      onChange={update("canvas")}
                      className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                    >
                      {CANVAS_SIZES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {needsIllustrationType && (
                  <div data-testid="contact-illustrationtype-wrap">
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                      Illustration Type
                    </label>
                    <select
                      data-testid="contact-illustrationtype"
                      value={form.illustrationType}
                      onChange={update("illustrationType")}
                      className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                    >
                      {ILLUSTRATION_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {needsLogoType && (
                  <div data-testid="contact-logotype-wrap">
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                      Logo Type
                    </label>
                    <select
                      data-testid="contact-logotype"
                      value={form.logoType}
                      onChange={update("logoType")}
                      className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                    >
                      {LOGO_TYPES.map((t) => (
                        <option key={t.name} value={t.name}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {needsUnits && (
                  <div data-testid="contact-units-wrap">
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                      Quantity
                    </label>
                    <select
                      data-testid="contact-units"
                      value={form.units}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, units: Number(e.target.value) }))
                      }
                      className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                    >
                      {UNIT_QUANTITIES.map((q) => (
                        <option key={q} value={q}>
                          {q}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {needsCardStyle && (
                  <div data-testid="contact-cardstyle-wrap">
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                      Which style?
                    </label>
                    <select
                      data-testid="contact-cardstyle"
                      value={form.cardStyle}
                      onChange={update("cardStyle")}
                      className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                    >
                      {CARD_STYLES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {needsQty && !hideQtyForEbook && (
                  <div data-testid="contact-qty-wrap">
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                      Quantity
                    </label>
                    <select
                      data-testid="contact-qty"
                      value={form.qty}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, qty: Number(e.target.value) }))
                      }
                      className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                    >
                      {(needsComicPages || needsChildrenPages
                        ? [25, 50, 100, 250]
                        : QUANTITIES
                      ).map((q) => (
                        <option key={q} value={q}>
                          {q}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(needsComicPages || needsChildrenPages) && (
                  <div data-testid="contact-pages-wrap">
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                      Pages
                    </label>
                    <select
                      data-testid="contact-pages"
                      value={form.pages}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, pages: Number(e.target.value) }))
                      }
                      className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white"
                    >
                      {pagesOptions.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                    Project Details
                  </label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Tell us about your idea, timeline and budget…"
                    className="w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white resize-none"
                  />
                </div>

                {form.service && (
                  <div
                    data-testid="contact-estimate"
                    className="brutal-border bg-[#EAF1F8] px-5 py-4 flex items-center justify-between gap-4"
                  >
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2530]/70">
                      Estimated Total
                    </span>
                    {estimate != null ? (
                      <span
                        data-testid="contact-estimate-value"
                        className="text-lg font-extrabold text-[#3E6FA8]"
                      >
                        ${estimate.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-sm font-bold text-[#1F2530]/70">
                        Custom quote on request
                      </span>
                    )}
                  </div>
                )}

                {needsDeposit && form.service && (
                  <p
                    data-testid="contact-deposit-note"
                    className="text-sm font-semibold italic text-red-600"
                  >
                    *Please Note: A 50% deposit is charged now. The remaining balance is
                    due upon order completion. Your order will be delivered once full
                    payment is received.
                  </p>
                )}

                {purchaseError && (
                  <p data-testid="contact-purchase-error" className="text-sm font-bold text-red-600">
                    {purchaseError}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    data-testid="contact-purchase"
                    onClick={handlePurchase}
                    disabled={purchasing}
                    className="w-full sm:w-auto brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-8 py-4 flex items-center justify-center gap-3 disabled:opacity-60"
                  >
                    <CreditCard size={20} strokeWidth={2.5} />
                    {purchasing ? "Redirecting…" : "Purchase"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* SIDE INFO */}
          <div className="lg:col-span-5 space-y-6">
            <a
              data-testid="contact-direct-email"
              href={`mailto:${BUSINESS_EMAIL}`}
              className="block brutal-border brutal-shadow brutal-press bg-[#E5E7EB] p-6 sm:p-8"
            >
              <Mail size={32} strokeWidth={2.5} className="text-[#3E6FA8]" />
              <p className="mt-4 text-xs font-bold uppercase tracking-[0.2em]">Email Us Directly</p>
              <p className="font-display text-3xl sm:text-4xl break-words mt-1">{BUSINESS_EMAIL}</p>
            </a>

            <div className="brutal-border brutal-shadow bg-[#475569] text-white p-6 sm:p-8 space-y-5">
              <div className="flex items-start gap-4">
                <Clock size={24} strokeWidth={2.5} className="text-[#E5E7EB] shrink-0 mt-1" />
                <div>
                  <p className="font-display text-2xl leading-none">Response Time</p>
                  <p className="font-medium text-white/80 mt-1">Within 1–2 business days.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={24} strokeWidth={2.5} className="text-[#E5E7EB] shrink-0 mt-1" />
                <div>
                  <p className="font-display text-2xl leading-none">Working Worldwide</p>
                  <p className="font-medium text-white/80 mt-1">Remote commissions, delivered digitally.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Hourglass size={24} strokeWidth={2.5} className="text-[#E5E7EB] shrink-0 mt-1" />
                <div>
                  <p className="font-display text-2xl leading-none">Turnover Timeframe</p>
                  <p className="font-medium text-white/80 mt-1">Within 7–14 business days.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
